import {
    useCallback,
    useState,
    useMemo,
    type FC,
    useRef,
    useId
} from "react";
import type {
    View
} from "react-native";
import type IDropdownMenuProps from "./type";
import type {
    DropdownMenuContextType
} from "./type";
import DropdownMenuCheckboxItem from "./components/checkboxItem";
import DropdownMenuContent from "./components/content";
import DropdownMenuEmpty from "./components/empty";
import DropdownMenuGroup from "./components/group";
import DropdownMenuItem from "./components/item";
import DropdownMenuLabel from "./components/label";
import DropdownMenuRadioGroup from "./components/radioGroup";
import DropdownMenuRadioItem from "./components/radioItem";
import DropdownMenuSeparator from "./components/separator";
import DropdownMenuShortcut from "./components/shortcut";
import DropdownMenuStatus from "./components/status";
import DropdownMenuSub from "./components/sub";
import DropdownMenuSubContent from "./components/subContent";
import DropdownMenuSubTrigger from "./components/subTrigger";
import DropdownMenuTrigger from "./components/trigger";
import {
    DropdownMenuContext
} from "./context";
import {
    useDropdownMenuRootState
} from "./hook";
import {
    usePointerType
} from "../../helpers/pointer";

const DropdownMenu: FC<IDropdownMenuProps> = ({
    isDefaultOpen = false,
    isOpen: isOpenProp,
    align = "start",
    side = "bottom",
    customLocalize,
    onOpenChange,
    customTheme,
    children,
    title
}) => {
    const pointerType = usePointerType();

    const triggerId = useId();

    const contentId = useId();

    const anchorRef = useRef<View | null>(null);

    const pointerAnchorRef = useRef<View | null>(null);

    const [
        triggerWidth,
        setTriggerWidth
    ] = useState(0);

    const {
        handleOpenChange,
        handleTabKey,
        handleClose,
        openMode,
        isOpen
    } = useDropdownMenuRootState({
        isOpen: isOpenProp,
        isDefaultOpen,
        onOpenChange,
        anchorRef
    });

    const handleTriggerWidthChange = useCallback((width: number) => {
        setTriggerWidth((previousWidth) => previousWidth === width ? previousWidth : width);
    }, []);

    const handlePointerChange = useCallback(() => {}, []);

    const value = useMemo<DropdownMenuContextType>(() => ({
        isSheet: pointerType === "coarse",
        handleTriggerWidthChange,
        handlePointerChange,
        isDisabled: false,
        handleOpenChange,
        kind: "dropdown",
        pointerAnchorRef,
        customLocalize,
        pointer: null,
        handleTabKey,
        triggerWidth,
        customTheme,
        handleClose,
        anchorRef,
        contentId,
        triggerId,
        openMode,
        isOpen,
        align,
        title,
        side
    }), [
        handleTriggerWidthChange,
        handlePointerChange,
        handleOpenChange,
        customLocalize,
        handleTabKey,
        triggerWidth,
        customTheme,
        handleClose,
        pointerType,
        contentId,
        triggerId,
        openMode,
        isOpen,
        align,
        title,
        side
    ]);

    return <DropdownMenuContext.Provider
        value={value}
    >
        {children}
    </DropdownMenuContext.Provider>;
};
export default Object.assign(DropdownMenu, {
    CheckboxItem: DropdownMenuCheckboxItem,
    RadioGroup: DropdownMenuRadioGroup,
    SubContent: DropdownMenuSubContent,
    SubTrigger: DropdownMenuSubTrigger,
    RadioItem: DropdownMenuRadioItem,
    Separator: DropdownMenuSeparator,
    Shortcut: DropdownMenuShortcut,
    Content: DropdownMenuContent,
    Trigger: DropdownMenuTrigger,
    Status: DropdownMenuStatus,
    Empty: DropdownMenuEmpty,
    Group: DropdownMenuGroup,
    Label: DropdownMenuLabel,
    Item: DropdownMenuItem,
    Sub: DropdownMenuSub
});
